type CaptureStreamVideoElement = HTMLVideoElement & {
  captureStream?: () => MediaStream;
  mozCaptureStream?: () => MediaStream;
};

const clampVideoTime = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const canBrowserProcessVideoForUpload = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof MediaRecorder === 'undefined') {
    return false;
  }

  const video = document.createElement('video') as CaptureStreamVideoElement;
  return typeof video.captureStream === 'function' || typeof video.mozCaptureStream === 'function';
};

interface RecordVideoBlobOptions {
  startTime: number;
  endTime?: number;
  preferredMimeTypes: string[];
  metadataErrorMessage: string;
  processingErrorMessage: string;
  emptyResultErrorMessage: string;
  unsupportedErrorMessage: string;
  recorderOptions?: Omit<MediaRecorderOptions, 'mimeType'>;
  onProgress?: (pct: number) => void;
}

const readVideoBlobDuration = (sourceBlob: Blob, errorMessage: string): Promise<number> =>
  new Promise<number>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error(errorMessage));
      return;
    }

    const sourceUrl = URL.createObjectURL(sourceBlob);
    const video = document.createElement('video');

    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(sourceUrl);
    };

    video.preload = 'metadata';
    video.playsInline = true;
    video.addEventListener('loadedmetadata', () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      cleanup();
      if (duration > 0) {
        resolve(duration);
        return;
      }
      reject(new Error(errorMessage));
    }, { once: true });
    video.addEventListener('error', () => {
      cleanup();
      reject(new Error(errorMessage));
    }, { once: true });
    video.src = sourceUrl;
  });

const recordVideoBlob = (sourceBlob: Blob, options: RecordVideoBlobOptions): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error(options.processingErrorMessage));
      return;
    }

    const sourceUrl = URL.createObjectURL(sourceBlob);
    const video = document.createElement('video') as CaptureStreamVideoElement;
    const sourceMimeType = sourceBlob.type.split(';')[0].trim() || 'video/webm';
    let captureStream: MediaStream | null = null;
    let recorder: MediaRecorder | null = null;
    let rafId: number | null = null;
    let settled = false;
    const chunks: BlobPart[] = [];

    const cleanup = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (captureStream) {
        captureStream.getTracks().forEach((track) => track.stop());
        captureStream = null;
      }
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (video.parentNode) {
        video.parentNode.removeChild(video);
      }
      URL.revokeObjectURL(sourceUrl);
    };

    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error instanceof Error ? error : new Error(options.processingErrorMessage));
    };

    const finish = (blob: Blob) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    };

    const seekTo = (time: number) => new Promise<void>((resolveSeek, rejectSeek) => {
      const handleSeeked = () => {
        video.removeEventListener('error', handleSeekError);
        resolveSeek();
      };
      const handleSeekError = () => {
        video.removeEventListener('seeked', handleSeeked);
        rejectSeek(new Error('定位裁剪区间失败，请重新尝试。'));
      };

      video.addEventListener('seeked', handleSeeked, { once: true });
      video.addEventListener('error', handleSeekError, { once: true });
      video.currentTime = time;
    });

    const monitorPlayback = (startTime: number, endTime: number) => {
      if (settled) return;
      const safeCurrentTime = Math.min(video.currentTime, endTime);
      const totalDuration = Math.max(endTime - startTime, 0.05);
      const progress = Math.max(0, Math.min(99, Math.round(((safeCurrentTime - startTime) / totalDuration) * 100)));
      options.onProgress?.(progress);
      if (video.currentTime >= endTime || video.ended) {
        video.pause();
        if (recorder && recorder.state !== 'inactive') {
          recorder.stop();
        } else {
          fail(new Error(options.processingErrorMessage));
        }
        return;
      }
      rafId = window.requestAnimationFrame(() => monitorPlayback(startTime, endTime));
    };

    video.preload = 'auto';
    video.playsInline = true;
    video.volume = 0;
    video.style.position = 'fixed';
    video.style.left = '-9999px';
    video.style.top = '-9999px';
    video.style.width = '1px';
    video.style.height = '1px';
    document.body.appendChild(video);

    video.addEventListener('loadedmetadata', async () => {
      try {
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        if (duration <= 0) {
          throw new Error(options.metadataErrorMessage);
        }

        const maxStartTime = Math.max(0, duration - 0.05);
        const requestedEndTime = typeof options.endTime === 'number' ? options.endTime : duration;
        const startTime = clampVideoTime(options.startTime, 0, maxStartTime);
        const endTime = clampVideoTime(requestedEndTime, Math.min(duration, startTime + 0.05), duration);
        if (endTime - startTime <= 0.05) {
          throw new Error(options.processingErrorMessage);
        }

        const captureFactory =
          typeof video.captureStream === 'function'
            ? () => video.captureStream()
            : typeof video.mozCaptureStream === 'function'
              ? () => video.mozCaptureStream()
              : null;

        if (!captureFactory) {
          throw new Error(options.unsupportedErrorMessage);
        }

        await seekTo(startTime);
        captureStream = captureFactory();

        const preferredMimeType = options.preferredMimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
        const recorderOptions: MediaRecorderOptions = { ...(options.recorderOptions ?? {}) };
        if (preferredMimeType) {
          recorderOptions.mimeType = preferredMimeType;
        }

        recorder = Object.keys(recorderOptions).length > 0
          ? new MediaRecorder(captureStream, recorderOptions)
          : new MediaRecorder(captureStream);

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        recorder.onerror = () => fail(new Error(options.processingErrorMessage));
        recorder.onstop = () => {
          const recordedMimeType = recorder?.mimeType?.split(';')[0].trim() || sourceMimeType;
          const trimmedBlob = new Blob(chunks, { type: recordedMimeType });
          if (trimmedBlob.size === 0) {
            fail(new Error(options.emptyResultErrorMessage));
            return;
          }
          options.onProgress?.(100);
          finish(trimmedBlob);
        };

        options.onProgress?.(0);
        recorder.start(100);
        await video.play();
        monitorPlayback(startTime, endTime);
      } catch (error) {
        fail(error);
      }
    }, { once: true });

    video.addEventListener('error', () => fail(new Error(options.metadataErrorMessage)), { once: true });
    video.src = sourceUrl;
  });

const getPreferredRecordingMimeTypes = (sourceMimeType: string) => {
  const normalizedSourceMimeType = sourceMimeType.split(';')[0].trim() || 'video/webm';
  return [
    normalizedSourceMimeType,
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm'
  ].filter((mimeType, index, list) => !!mimeType && list.indexOf(mimeType) === index);
};

export function exportTrimmedVideoBlob(sourceBlob: Blob, trimStart: number, trimEnd: number): Promise<Blob> {
  return recordVideoBlob(sourceBlob, {
    startTime: trimStart,
    endTime: trimEnd,
    preferredMimeTypes: getPreferredRecordingMimeTypes(sourceBlob.type),
    metadataErrorMessage: '无法读取录制视频，请重新录制。',
    processingErrorMessage: '导出裁剪视频失败，请重试。',
    emptyResultErrorMessage: '裁剪后视频为空，请重新尝试。',
    unsupportedErrorMessage: '当前浏览器暂不支持导出裁剪后的视频，请更换浏览器或直接上传原视频。'
  });
}

export function transcodeVideoBlobForWebPlayback(sourceBlob: Blob, onProgress?: (pct: number) => void): Promise<Blob> {
  return recordVideoBlob(sourceBlob, {
    startTime: 0,
    preferredMimeTypes: [
      'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ],
    metadataErrorMessage: '无法读取视频，请重新选择视频。',
    processingErrorMessage: '视频转码失败，请稍后重试。',
    emptyResultErrorMessage: '转码后视频为空，请重新选择视频。',
    unsupportedErrorMessage: '当前浏览器暂不支持自动转码视频，请改用 MP4 文件或更换浏览器后重试。',
    onProgress
  });
}

export async function compressVideoBlobToMaxSize(
  sourceBlob: Blob,
  maxBytes: number,
  onProgress?: (pct: number) => void
): Promise<Blob> {
  const maxMegabytes = Math.max(1, Math.round(maxBytes / 1024 / 1024));
  if (sourceBlob.size <= maxBytes) {
    onProgress?.(100);
    return sourceBlob;
  }

  const duration = await readVideoBlobDuration(sourceBlob, '无法读取视频时长，请重新选择视频。');
  if (duration <= 0) {
    throw new Error('无法读取视频时长，请重新选择视频。');
  }

  const minAudioBitsPerSecond = 32_000;
  const maxAudioBitsPerSecond = 96_000;
  const minVideoBitsPerSecond = 160_000;
  const attemptRatios = [0.88, 0.74, 0.62];
  const totalAttempts = attemptRatios.length;
  let workingBlob = sourceBlob;
  onProgress?.(0);

  for (let i = 0; i < attemptRatios.length; i++) {
    const ratio = attemptRatios[i];

    const safeTargetBytes = Math.max(Math.floor(maxBytes * ratio), 1_024 * 1_024);
    const targetBitsPerSecond = Math.max(
      Math.floor((safeTargetBytes * 8) / duration),
      minAudioBitsPerSecond + minVideoBitsPerSecond
    );
    const audioBitsPerSecond = Math.min(
      maxAudioBitsPerSecond,
      Math.max(minAudioBitsPerSecond, Math.floor(targetBitsPerSecond * 0.12))
    );
    const videoBitsPerSecond = Math.max(minVideoBitsPerSecond, targetBitsPerSecond - audioBitsPerSecond);

    const compressedBlob = await recordVideoBlob(workingBlob, {
      startTime: 0,
      endTime: duration,
      preferredMimeTypes: getPreferredRecordingMimeTypes(workingBlob.type || sourceBlob.type),
      metadataErrorMessage: '无法读取视频时长，请重新选择视频。',
      processingErrorMessage: '自动压缩视频失败，请稍后重试。',
      emptyResultErrorMessage: '压缩后视频为空，请重新选择视频。',
      unsupportedErrorMessage: `当前浏览器暂不支持自动压缩视频，请先手动压缩到 ${maxMegabytes}MB 以内后再上传。`,
      onProgress: (pct) => {
        const overallProgress = Math.round(((i + pct / 100) / totalAttempts) * 100);
        onProgress?.(overallProgress);
      },
      recorderOptions: {
        audioBitsPerSecond,
        videoBitsPerSecond
      }
    });

    if (compressedBlob.size <= maxBytes) {
      onProgress?.(100);
      return compressedBlob;
    }

    if (compressedBlob.size < workingBlob.size) {
      workingBlob = compressedBlob;
    }
  }

  if (workingBlob.size <= maxBytes) {
    onProgress?.(100);
    return workingBlob;
  }

  onProgress?.(100);
  throw new Error(`自动压缩后仍超过 ${maxMegabytes}MB，请缩短视频时长后重试。`);
}
